import { useState, useEffect, useRef } from 'react';

const BAR_COUNT = 5;

/**
 * VoiceLevelIndicator - Shows animated volume bars during voice recording.
 * Uses the AudioContext's AnalyserNode to display real-time mic levels.
 *
 * Props:
 *   audioContext - active AudioContext (from useSpeechToText)
 *   mediaStream - active MediaStream (from useSpeechToText)
 *   isRecording - whether recording is active
 */
const VoiceLevelIndicator = ({ audioContext, mediaStream, isRecording }) => {
  const [levels, setLevels] = useState(() => new Array(BAR_COUNT).fill(0));
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!isRecording || !audioContext || !mediaStream) {
      setLevels(new Array(BAR_COUNT).fill(0));
      return;
    }

    try {
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 64;
      analyser.smoothingTimeConstant = 0.6;
      analyserRef.current = analyser;

      const source = audioContext.createMediaStreamSource(mediaStream);
      source.connect(analyser);
      sourceRef.current = source;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const update = () => {
        analyser.getByteFrequencyData(dataArray);

        const binSize = Math.floor(dataArray.length / BAR_COUNT);
        const bars = [];
        for (let i = 0; i < BAR_COUNT; i++) {
          let sum = 0;
          for (let j = 0; j < binSize; j++) {
            sum += dataArray[i * binSize + j];
          }
          bars.push(Math.min(1, (sum / binSize) / 180));
        }
        setLevels(bars);
        rafRef.current = requestAnimationFrame(update);
      };

      rafRef.current = requestAnimationFrame(update);
    } catch (err) {
      console.error('VoiceLevelIndicator: setup failed', err);
    }

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
      if (analyserRef.current) {
        analyserRef.current = null;
      }
    };
  }, [isRecording, audioContext, mediaStream]);

  if (!isRecording) return null;

  return (
    <div className="voice-level-indicator" aria-label="Voice level indicator">
      {levels.map((level, i) => (
        <div
          key={i}
          className="voice-level-bar"
          style={{
            height: `${Math.max(4, level * 24)}px`,
          }}
        />
      ))}
    </div>
  );
};

export default VoiceLevelIndicator;
